import type { Effect } from '../rules/effects.ts';
import type { DamageType } from '../rules/damage.ts';
import type { StatId } from './stats.ts';
import type { Degree } from '../rules/degrees.ts';
import { isDegree } from '../rules/degrees.ts';

/**
 * Encounter-scoped state: one combat or structured scene.
 *
 * Kept separate from CharacterState (the permanent sheet) and from SessionState
 * (the sitting). An encounter starts, runs some rounds, and ends; nothing here
 * outlives it except through explicit application.
 *
 * An encounter IS persisted across application restarts, so a table can stop
 * mid-combat and resume. "Scoped" refers to the rules lifetime of the data, not
 * to whether it is saved.
 *
 * Sources:
 *  - Rules for Survival p.17: a round lasts 10 seconds; each crawler gets two
 *    Actions in a structured scene.
 *  - Rules for Survival p.17: cooldown periods (per round / scene / hours /
 *    day / days / floor).
 *  - Rules for Survival p.43: Spells that last for combat rounds end during
 *    Combat Step 5: Clean Up.
 */
export const ENCOUNTER_SCHEMA_VERSION = 8;

/** RULE DCC-ENC-001 - two Actions per crawler per round (Rules p.17). */
export const ACTIONS_PER_ROUND = 2;

export type CooldownScope = 'round' | 'scene' | 'hours' | 'day' | 'days' | 'floor';

export interface CooldownRecord {
  /** Content ID of the Skill, Spell or item the cooldown belongs to. */
  sourceId: string;
  scope: CooldownScope;
  /** Only meaningful for the 'hours' and 'days' scopes. */
  amount?: number;
  /** The round the cooldown was started on, for round-scoped cooldowns. */
  startedOnRound: number;
  /**
   * In-game seconds left, for cooldowns measured in time.
   *
   * Absent on round-, scene- and floor-scoped cooldowns, which are not
   * durations at all. Counted down by `advanceTime`, so a saved crawler shows
   * how much of a 30-hour cooldown is left rather than a stamp that needs a
   * running total to interpret.
   */
  secondsRemaining?: number;
}

/**
 * Damage a Debuff deals at the end of each round.
 *
 * Source: Rules for Survival p.45, Table 8. Burned is "1d10+F Fire damage at
 * the end of each round"; Blood Trail and Drowning are "1d6+F". The shape is
 * therefore dice, optionally plus the Floor Number (F), with a damage type
 * where the table gives one.
 *
 * This is deliberately a small STRUCTURE, not a script. Anything a supplied
 * Debuff cannot express this way gets its own narrow handler rather than a
 * scripting language.
 */
export interface RecurringDamage {
  /** Dice notation, e.g. "1d10". */
  dice: string;
  /** True when the printed effect adds F, the Floor Number. */
  addFloor: boolean;
  /** Null when the table states no damage type. */
  damageType: DamageType | null;
  /** All Debuff damage bypasses DR (Rules p.46). */
  ignoresDamageResistance?: boolean;
}

/**
 * How an Effect ends.
 *
 * A CLOSED set, containing only the termination conditions Table 8 actually
 * prints (Rules for Survival pp.45-46). This is not a condition language: to
 * support a new printed duration, add a member here and handle it explicitly.
 *
 * `roundsRemaining` appears on 'after-rounds' ONLY. Conditions such as "until
 * cured" are not forced into a fake round count.
 */
export type ExpirationCondition =
  /** "Until the end of 2 rounds", "until the end of the next round". */
  | { kind: 'after-rounds'; roundsRemaining: number }
  /** "Until the end of combat", "until the end of the encounter". */
  | { kind: 'end-of-encounter' }
  /**
   * "or 5 minutes", "until the end of 10 minutes", "until the end of 1 hour",
   * "or 20 seconds". Held in SECONDS so sub-minute durations stay exact.
   */
  | { kind: 'elapsed-time'; seconds: number }
  /** Stunned: "Once you make a Check." */
  | { kind: 'after-next-check' }
  /** Staggered, Queasy: "At the end of the next Action you take." */
  | { kind: 'after-next-action' }
  /** Blood Trail: "cured with a bandage"; Poisoned: "treated with an antidote". */
  | { kind: 'cured'; treatment: string }
  /** Fatigued and the injuries: "Until the end of a long rest." */
  | { kind: 'end-of-rest'; rest: 'short' | 'long' | 'full-day' }
  /**
   * A Check the victim may make to end it. Burned: "As an Action, a victim may
   * perform a Dex Stat Check vs. Difficulty 10 + Floor to extinguish the
   * flames." Held: "Make a Str-Opposed Dex Stat Check or Escape Artist Skill
   * Check."
   */
  | {
      kind: 'check-to-end';
      /** Printed wording, kept short, for the interface to show. */
      description: string;
      statCheck: StatId | null;
      skillIds: string[];
      difficulty: 'ten-plus-floor' | 'opposed' | null;
      requiresAction: boolean;
    }
  /** Held: "Until you are released by whatever is holding you". */
  | { kind: 'released' }
  /** Dying: "or heal at least 1 HB slot"; Sepsis: "until you're healed". */
  | { kind: 'healed'; minimumHealthBarSlots: number }
  /** Terrified: "or you take at least 1 HB slot damage". */
  | { kind: 'damage-taken'; minimumHealthBarSlots: number }
  /** Take Down: "Use your 10ft Step to stand." An Action, not a Check. */
  | { kind: 'action-to-end'; description: string }
  /**
   * No printed trigger the application can detect. Drowning ends "until your
   * head is above water"; Rules p.44 also lets the GM end any Debuff early when
   * circumstances change, so this is always an available route.
   */
  | { kind: 'manual'; description?: string };

/**
 * An Effect currently in play, with every way it can end.
 *
 * The Effect ITSELF is stored here, not just its ID, so the encounter and the
 * active effect list can never disagree about what is in play.
 *
 * `endsWhen` is a LIST because the source often prints more than one
 * termination condition and means "whichever happens first". Burned ends at the
 * end of combat, OR after 5 minutes, OR on a successful Dex Stat Check. All
 * three are recorded; none is silently discarded.
 */
export interface TimedEffectRecord {
  effect: Effect;
  /** Any one of these ending the Effect ends it. */
  endsWhen: ExpirationCondition[];
  /** Applied during Clean Up, if the Debuff deals damage each round. */
  recurringDamage?: RecurringDamage;
}

/** The round-based condition, when there is one. */
export function roundsRemainingOf(record: TimedEffectRecord): number | null {
  const condition = record.endsWhen.find((c) => c.kind === 'after-rounds');
  return condition && condition.kind === 'after-rounds' ? condition.roundsRemaining : null;
}

/**
 * One combat or structured scene. ONLY the things that exist because a fight is
 * happening: the round number and the Actions left in it.
 *
 * Active Effects and cooldowns deliberately do NOT live here. Many of them
 * outlive the fight - Fatigued lasts until a long rest, a 30-hour cooldown
 * spans floors - so they belong to the crawler's runtime state (domain/runtime.ts),
 * which persists whether or not a fight is in progress.
 */
/**
 * RULE DCC-ENC-002 - the step a combat round is currently in (Rules p.32).
 *
 * The printed sequence has FIVE steps. Only four are persisted:
 *
 *   1. Mob Action Declaration   -> 'mob-declaration'
 *   2. Crawler Reaction Phase   -> 'crawler-reaction'
 *   3. Mob Action Resolution    -> 'mob-resolution'
 *   4. Crawler Action Phase     -> 'crawler-action'
 *   5. Clean Up                 -> NOT A PHASE
 *
 * CLEAN UP IS NOT PERSISTED, deliberately. It is a transaction, not a place to
 * be: it expires Effects, resolves recurring damage, regenerates Mana, resets
 * Actions, advances the round and advances the clock, and it commits Character,
 * Session and Runtime together. A persisted `cleanup` phase would be a stable
 * state in the middle of that transaction - exactly the half-applied crawler
 * the checkpoint architecture exists to prevent - and it would open a second
 * door to Mana regeneration. Leaving `crawler-action` runs Clean Up atomically
 * and arrives at `mob-declaration` of the next round.
 *
 * MOB RESOLUTION IS PERSISTED even though the GM runs the Mobs, because the
 * crawler is not a spectator during it: incoming damage lands, Debuffs apply,
 * and the crawler applies DR and marks their Health Bar. It is also the hard
 * ordering boundary a Healing Interrupt will need later - healing in
 * `crawler-reaction` resolves BEFORE damage in `mob-resolution`. That is
 * Foundation B; nothing here implements it.
 */
export type EncounterPhase =
  | 'mob-declaration'
  | 'crawler-reaction'
  | 'mob-resolution'
  | 'crawler-action';

export const ENCOUNTER_PHASES: readonly EncounterPhase[] = [
  'mob-declaration',
  'crawler-reaction',
  'mob-resolution',
  'crawler-action',
];

export function isEncounterPhase(value: unknown): value is EncounterPhase {
  return typeof value === 'string' && (ENCOUNTER_PHASES as readonly string[]).includes(value);
}

/**
 * RULE DCC-ENC-006 - how exposed the crawler is to one incoming attack.
 *
 * Rules p.35. A successful Evade against a `direct` attack avoids it; against
 * an area attack it reduces the damage to 50%, and in the splash zone to 25%,
 * rounded down. C1 RECORDS THE CLASSIFICATION ONLY - no multiplier is applied
 * anywhere, and where those fractions sit relative to DR is still an open
 * rules question.
 */
export type AttackExposure = 'direct' | 'aoe' | 'splash';

export const ATTACK_EXPOSURES: readonly AttackExposure[] = ['direct', 'aoe', 'splash'];

/**
 * ONE exposure check, shared by the request boundary and the persisted
 * validator. A second copy of the string union is a second thing to forget.
 */
export function isAttackExposure(value: unknown): value is AttackExposure {
  return typeof value === 'string' && (ATTACK_EXPOSURES as readonly string[]).includes(value);
}

/**
 * RULE DCC-ENC-006 - ONE attack currently aimed at this crawler.
 *
 * NOT A MOB RECORD. It carries no Health, no Dex, no position and no catalogue
 * link; the GM or the VTT still owns the enemy. It is a statement about the
 * crawler's exposure, which is the only part of a fight a character manager has
 * any business owning.
 *
 * ONE ENTRY PER ATTACK, never a count. Rules p.33: "The player rolls a separate
 * Evade Check against each attack directed at their crawler, so if 3 attacks
 * are directed at the crawler, the player makes 3 Evade Checks." Three attacks
 * can succeed, fail and succeed, and a single record with a count and one
 * status cannot hold that. Three identical spear thrusts are three rows.
 */
/**
 * RULE DCC-ENC-007 - what an Evade Check against one attack left behind.
 *
 * TWO FIELDS, and no more. `total` is frozen because it is what was rolled;
 * `degree` because Rules p.32 makes a natural 1 mechanically distinct - twice
 * the damage from that attack and a Major Injury after the fight - so a
 * `success: boolean` would erase the difference between an ordinary miss and a
 * catastrophe. Success is READ from the degree by `degreeIsSuccess`.
 *
 * Deliberately absent: `success` (derivable), the Difficulty (already on the
 * declaration), the margin, a damage multiplier, a Major Injury flag, and the
 * roll-history entry id. Every one of those is a second copy of something that
 * can drift from the first - and history is capped at ten entries, so an id
 * pointing into it is a durable dependency on something that rolls off.
 *
 * The dice faces live in Session roll history, which is where a player looks to
 * see what happened. This is only what the ENCOUNTER still needs afterwards.
 */
export interface IncomingAttackEvadeResult {
  total: number;
  degree: Degree;
}

export interface IncomingAttackDeclaration {
  /** Unique within the encounter. Attack identity, NOT Mob identity. */
  id: string;
  /** Free text: "Goblin 2 - Spear". Descriptive; never resolved against content. */
  sourceLabel: string;
  /**
   * The GM-supplied Difficulty for the crawler's Evade Check against THIS
   * attack. Supplied rather than derived, for the same reason
   * `resolveAttackAction` takes a target's Evade score: no Mob is modelled, and
   * adding Mob Dex and Floor fields so the application could compute it would
   * be building the Mob model this project has repeatedly declined to build.
   */
  evadeDifficulty: number;
  exposure: AttackExposure;
  /** RULE DCC-ENC-007. Null until the crawler takes the Evade Interrupt. */
  evadeResult: IncomingAttackEvadeResult | null;
}

export interface EncounterState {
  schemaVersion: number;
  characterId: string;
  startedAt: string;
  /** Rounds are numbered from 1. */
  round: number;
  /** Actions left this round, keyed by Actor ID. */
  actionsRemaining: Record<string, number>;
  /** RULE DCC-ENC-002 - which step of the round is in progress. */
  phase: EncounterPhase;
  /**
   * RULE DCC-ENC-006 - attacks the GM has announced against this crawler.
   *
   * OPTIONAL AUTOMATION INPUT. An empty list is completely normal and always
   * legal: a player whose GM runs the Mobs on paper never has to type anything
   * here, and the phase advances regardless. The moment declarations become
   * required to play a round, the character sheet has turned into mandatory
   * encounter bookkeeping.
   *
   * Cleared by the completed-round transition. Last round's threats are not
   * this round's.
   */
  incomingAttacks: IncomingAttackDeclaration[];
}

/**
 * A NEW fight starts at Mob Action Declaration, which is Step 1.
 *
 * Not to be confused with the 5 -> 6 migration default, which is
 * `crawler-action` for a completely different reason - see the migration.
 * Starting a fight does no round work: no time passes, no Mana regenerates, no
 * Effects expire, and there is no surprise Action (Rules p.32 grants one; it is
 * a deferred source-backed exception, not implemented here).
 */
export function createEncounter(characterId: string, startedAt: string): EncounterState {
  return {
    schemaVersion: ENCOUNTER_SCHEMA_VERSION,
    characterId,
    startedAt,
    round: 1,
    actionsRemaining: { [characterId]: ACTIONS_PER_ROUND },
    phase: 'mob-declaration',
    // Starting a fight announces nothing. The GM has not spoken yet.
    incomingAttacks: [],
  };
}

export interface EncounterValidation {
  valid: boolean;
  errors: string[];
}

export function validateEncounter(value: unknown): EncounterValidation {
  const errors: string[] = [];
  if (typeof value !== 'object' || value === null) {
    return { valid: false, errors: ['Encounter record is not an object.'] };
  }
  const e = value as Record<string, unknown>;
  if (typeof e['characterId'] !== 'string' || e['characterId'].length === 0) {
    errors.push('Encounter is missing a characterId.');
  }
  if (typeof e['schemaVersion'] !== 'number') {
    errors.push('Encounter is missing a schemaVersion.');
  } else if (e['schemaVersion'] > ENCOUNTER_SCHEMA_VERSION) {
    errors.push('Encounter was saved by a newer version of the app.');
  }
  if (typeof e['round'] !== 'number' || !Number.isInteger(e['round']) || (e['round'] as number) < 1) {
    errors.push('round must be a whole number of at least 1.');
  }
  if (typeof e['actionsRemaining'] !== 'object' || e['actionsRemaining'] === null) {
    errors.push('actionsRemaining must be an object.');
  } else if (Array.isArray(e['actionsRemaining'])) {
    // An array passes `typeof === 'object'`, and every key would then be an
    // index rather than an Actor ID.
    errors.push('actionsRemaining must be keyed by Actor ID, not a list.');
  } else {
    // Every value is a count of Actions. A string, a fraction or a negative
    // number here means `spendAction` compares against something that is not a
    // number of Actions, which is worse than refusing the record.
    for (const [actorId, remaining] of Object.entries(e['actionsRemaining'] as object)) {
      if (typeof remaining !== 'number' || !Number.isInteger(remaining) || remaining < 0) {
        errors.push(`actionsRemaining["${actorId}"] must be a whole number of at least 0.`);
      }
    }
  }
  // EXACTLY ONE OF THE FOUR, and no repair. Migration is where a missing phase
  // gets supplied, because migration knows which version it is looking at and
  // can therefore make a defensible choice. A validator that quietly filled in
  // a default would hide a malformed CURRENT record behind a plausible value,
  // and the crawler would then play a round in a phase nobody chose.
  // RULE DCC-ENC-006 - every declaration, individually.
  //
  // NO REPAIR. Migration supplies `[]` for a version 6 record because it knows
  // which version it is looking at. A validator that quietly dropped a
  // malformed entry would hide a corrupted save behind a plausible list, and
  // C2 would then roll Evade against attacks that had silently changed.
  const declarations = e['incomingAttacks'];
  if (!Array.isArray(declarations)) {
    errors.push('incomingAttacks must be a list.');
  } else {
    const seen = new Set<string>();
    declarations.forEach((entry, index) => {
      if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) {
        errors.push(`incomingAttacks[${index}] must be an attack declaration.`);
        return;
      }
      const attack = entry as Record<string, unknown>;
      const id = attack['id'];
      if (typeof id !== 'string' || id.trim() === '') {
        errors.push(`incomingAttacks[${index}] needs an id.`);
      } else if (seen.has(id)) {
        // IDENTITY IS THE POINT. Two attacks sharing an id cannot be resolved
        // independently, which is the whole reason this is a list of attacks
        // rather than a count.
        errors.push(`incomingAttacks[${index}] repeats the id "${id}".`);
      } else {
        seen.add(id);
      }
      const label = attack['sourceLabel'];
      if (typeof label !== 'string' || label.trim() === '') {
        errors.push(`incomingAttacks[${index}] needs a source.`);
      }
      const difficulty = attack['evadeDifficulty'];
      if (
        typeof difficulty !== 'number' ||
        !Number.isInteger(difficulty) ||
        difficulty < 1
      ) {
        errors.push(`incomingAttacks[${index}] needs a whole Evade Difficulty of at least 1.`);
      }
      // RULE DCC-ENC-007 - the result field must be PRESENT, and either null or
      // a well-formed result. Migration supplies null for a version 7 record;
      // validation does not repair a malformed current one, because a silently
      // nulled result is a crawler's Critical Fail quietly becoming "not rolled
      // yet".
      if (!('evadeResult' in attack)) {
        errors.push(`incomingAttacks[${index}] is missing evadeResult (use null).`);
      } else if (attack['evadeResult'] !== null) {
        const result = attack['evadeResult'];
        if (typeof result !== 'object' || result === null || Array.isArray(result)) {
          errors.push(`incomingAttacks[${index}].evadeResult must be a result or null.`);
        } else {
          const total = (result as Record<string, unknown>)['total'];
          if (typeof total !== 'number' || !Number.isInteger(total)) {
            errors.push(`incomingAttacks[${index}].evadeResult needs a whole total.`);
          }
          // `isDegree`, NOT `degree in DEGREE_LABELS`. `in` walks the
          // prototype chain, so 'toString', 'constructor' and '__proto__' would
          // all have passed - and a Record of presentation LABELS was never an
          // enum authority in the first place.
          const degree = (result as Record<string, unknown>)['degree'];
          if (!isDegree(degree)) {
            errors.push(
              `incomingAttacks[${index}].evadeResult has an unknown degree ` +
                `(${JSON.stringify(degree)}).`,
            );
          }
        }
      }
      if (!isAttackExposure(attack['exposure'])) {
        errors.push(
          `incomingAttacks[${index}] must be ${ATTACK_EXPOSURES.join(', ')} ` +
            `(found ${JSON.stringify(attack['exposure'])}).`,
        );
      }
    });
  }

  if (!isEncounterPhase(e['phase'])) {
    errors.push(
      `phase must be one of ${ENCOUNTER_PHASES.join(', ')} (found ${JSON.stringify(e['phase'])}).`,
    );
  }
  return { valid: errors.length === 0, errors };
}

/**
 * 1 -> 2: timed Effects now carry the Effect itself rather than only its ID.
 *
 * A version 1 record holds only an ID, so the Effect it referred to cannot be
 * reconstructed. Those timers are therefore dropped. This is an accepted
 * PRE-RELEASE schema-compatibility tradeoff, taken because version 1 encounters
 * only ever existed in development builds: dropping a timer is preferable to
 * inventing an Effect that was never recorded.
 *
 * It is NOT because encounters are transient. Encounter state is deliberately
 * persisted across restarts, and a future migration that CAN preserve its data
 * must do so rather than following this precedent.
 */
export function migrateEncounterRecord(value: unknown): unknown {
  const record = migrateEncounterThroughV4(value);
  if (typeof record !== 'object' || record === null) return record;
  const transferred = transferEncounterOwnedData(record as Record<string, unknown>);
  if (typeof transferred !== 'object' || transferred === null) return transferred;
  return applyEvadeResultDefault(
    applyIncomingAttacksDefault(
      applyEncounterPhaseDefault(transferred as Record<string, unknown>),
    ),
  );
}

/**
 * The encounter chain UP TO version 4, stopping before the ownership transfer.
 *
 * SPLIT DELIBERATELY, because of a defect this had in its first form. The
 * runtime rescue used to read `timedEffects` and `cooldowns` straight off the
 * raw nested record and hoist them before any of this ran. That is only correct
 * for a version 4 encounter. A version 1 record holds Effects that the 1 -> 2
 * step deliberately DROPS as unreconstructable; a version 2 record holds
 * `roundsRemaining` that 2 -> 3 turns into `endsWhen`; a version 3 record holds
 * elapsed times in `minutes` that 3 -> 4 turns into `seconds`. Hoisting any of
 * those raw put obsolete shapes into `runtime.active` while claiming to have
 * rescued them.
 *
 * So the rescue asks for THIS - a payload already carried to version 4, whose
 * `timedEffects` and `cooldowns` are in the current representation - and only
 * then lifts them out. `migrateEncounterRecord` is this plus the transfer, so
 * there is one chain, not two.
 */
export function migrateEncounterThroughV4(value: unknown): unknown {
  if (typeof value !== 'object' || value === null) return value;
  const record = { ...(value as Record<string, unknown>) };
  if (record['schemaVersion'] === 1) {
    record['timedEffects'] = [];
    record['schemaVersion'] = 2;
  }
  if (record['schemaVersion'] === 2) {
    // 2 -> 3: `roundsRemaining: number | null` becomes an `endsWhen` list, so
    // durations the source does not measure in rounds stop being forced into
    // round counts. A version 2 record maps cleanly: a number becomes
    // 'after-rounds', and null meant "until the encounter ends".
    const effects = Array.isArray(record['timedEffects']) ? record['timedEffects'] : [];
    record['timedEffects'] = effects.map((entry) => {
      if (typeof entry !== 'object' || entry === null) return entry;
      const e = entry as Record<string, unknown>;
      const rounds = e['roundsRemaining'];
      const endsWhen =
        typeof rounds === 'number'
          ? [{ kind: 'after-rounds', roundsRemaining: rounds }]
          : [{ kind: 'end-of-encounter' }];
      const { roundsRemaining: _dropped, ...rest } = e;
      return { ...rest, endsWhen };
    });
    record['schemaVersion'] = 3;
  }
  if (record['schemaVersion'] === 3) {
    // 3 -> 4: elapsed-time moves from minutes to seconds, so "20 seconds"
    // (Enraged, Rules p.45) can be stated exactly rather than as a fraction.
    const effects = Array.isArray(record['timedEffects']) ? record['timedEffects'] : [];
    record['timedEffects'] = effects.map((entry) => {
      if (typeof entry !== 'object' || entry === null) return entry;
      const e = entry as Record<string, unknown>;
      const endsWhen = Array.isArray(e['endsWhen']) ? e['endsWhen'] : [];
      return {
        ...e,
        endsWhen: endsWhen.map((c) => {
          if (typeof c !== 'object' || c === null) return c;
          const condition = c as Record<string, unknown>;
          if (condition['kind'] !== 'elapsed-time') return condition;
          const { minutes, ...rest } = condition;
          return { ...rest, seconds: typeof minutes === 'number' ? minutes * 60 : 0 };
        }),
      };
    });
    record['schemaVersion'] = 4;
  }
  return record;
}

/**
 * 4 -> 5: `timedEffects` and `cooldowns` LEAVE the encounter.
 *
 * This is the version bump that accompanied the introduction of RuntimeState.
 * Up to version 4 the encounter owned active Effects and cooldowns and had its
 * own file, which was deleted when the fight ended - so a Fatigued crawler
 * stopped being Fatigued and a 30-hour cooldown vanished with the scene
 * (docs/ARCHITECTURE.md). Version 5 holds only what exists because a fight is
 * happening: the round and the Actions in it.
 *
 * STRIPPING THOSE FIELDS IS SAFE ONLY BECAUSE SOMETHING ELSE SAVED THEM FIRST.
 * An encounter migration cannot hoist them - the outer runtime is not in scope.
 * `migrateRuntimeRecord` takes a `migrateEncounterThroughV4` payload, lifts its
 * Effects and cooldowns onto the runtime, and only then applies this. Calling
 * this on a legacy nested encounter by any other route drops real data.
 */
export function transferEncounterOwnedData(record: Record<string, unknown>): unknown {
  if (record['schemaVersion'] !== 4) return record;
  const { timedEffects: _effects, cooldowns: _cooldowns, ...rest } = record;
  return applyEncounterPhaseDefault({ ...rest, schemaVersion: 5 });
}

/**
 * 5 -> 6: the round gained a PHASE (RULE DCC-ENC-002).
 *
 * A version 5 encounter has no phase and there is no way to work out what it
 * was, because the application that wrote it did not have the concept. So this
 * is a COMPATIBILITY MAPPING, not a reconstruction: it does not claim the fight
 * was really in Step 4.
 *
 * `crawler-action` is chosen because it is the phase in which the pre-phase
 * application actually behaved. That build showed the crawler's ordinary Action
 * controls and an "End round" button, which is Step 4 and the Clean Up that
 * follows it. Landing an in-progress fight there:
 *
 *  - lets the player keep spending whatever Actions are left, which is what
 *    they could do a moment before the upgrade;
 *  - keeps "End round" working immediately, from the one phase it is legal in;
 *  - does NOT hand out a retroactive Reaction opportunity the old build never
 *    offered - starting at `crawler-reaction` would invent one;
 *  - does NOT push a live fight back through Mob declaration and resolution
 *    steps that were never tracked and cannot be answered.
 *
 * NOT to be confused with `createEncounter`, which starts a NEW fight at
 * `mob-declaration`. Those two defaults answer different questions and must
 * never be collapsed into one constant.
 *
 * Everything else is carried across untouched: the round, every
 * `actionsRemaining` entry, the crawler and the start time. Partially spent
 * Actions in particular survive - an upgrade is not a reason to hand a crawler
 * two fresh Actions mid-round.
 */
export function applyEncounterPhaseDefault(record: Record<string, unknown>): Record<string, unknown> {
  if (record['schemaVersion'] !== 5) return record;
  return { ...record, phase: 'crawler-action', schemaVersion: 6 };
}

/**
 * 6 -> 7: the round gained a list of INCOMING ATTACKS (RULE DCC-ENC-006).
 *
 * An empty list, and there is nothing to reconstruct: the build that wrote a
 * version 6 encounter had no concept of a declaration, so a fight in progress
 * genuinely had none recorded. Unlike the 5 -> 6 phase default, this is not
 * even a compatibility CHOICE - `[]` is the truthful answer.
 *
 * Everything else is carried untouched, the phase included. A v5 record
 * therefore travels 5 -> 6 (phase becomes `crawler-action`) -> 7 (empty list)
 * through the one chain, and the v5 mapping is not revisited here.
 */
export function applyIncomingAttacksDefault(
  record: Record<string, unknown>,
): Record<string, unknown> {
  if (record['schemaVersion'] !== 6) return record;
  return { ...record, incomingAttacks: [], schemaVersion: 7 };
}

/**
 * 7 -> 8: each declaration gained an Evade RESULT (RULE DCC-ENC-007).
 *
 * `null` for every one of them, and there is nothing to reconstruct: the build
 * that wrote a version 7 record could not execute a declaration-driven Evade,
 * so nothing had been rolled. Like 6 -> 7, this is not a compatibility choice -
 * `null` is the truthful answer.
 *
 * IT DOES NOT REPAIR. An entry that is not an object is passed through exactly
 * as it is, so `validateEncounter` can still reject it. Quietly wrapping
 * rubbish in a valid-looking declaration would turn a corrupt save into a
 * plausible one, which is worse than refusing to open it.
 */
export function applyEvadeResultDefault(
  record: Record<string, unknown>,
): Record<string, unknown> {
  if (record['schemaVersion'] !== 7) return record;
  const declarations = record['incomingAttacks'];
  if (!Array.isArray(declarations)) return { ...record, schemaVersion: 8 };
  return {
    ...record,
    schemaVersion: 8,
    incomingAttacks: declarations.map((entry) =>
      typeof entry === 'object' && entry !== null && !Array.isArray(entry)
        ? { ...(entry as Record<string, unknown>), evadeResult: null }
        : entry,
    ),
  };
}
